import { useMemo, useState, useEffect } from "react"
import { isEmpty, debounce } from "lodash"
import useResource from "hooks/resources"
import { SecuritySearchFilter } from "resources/filters"
import { IsoDate } from "helpers/types/dates"

const FOUND_SECURITIES_QUERY = `
  query ($modelId: String!, $filter: [SecuritySearchFilter!]!, $on: Date!, $currency: Currency) {
    model (id: $modelId) {
      securitySearch (filter: $filter, on: $on, currency: $currency) {
        count
      }
    }
  }
`

const DEBOUNCE_DELAY = 500

export default function useFilterResultsCount(
  filter: SecuritySearchFilter[],
  modelId: string | null,
  on: IsoDate,
  currency: string,
) {
  const [debouncedFilter, setDebouncedFilter] = useState(filter)

  const filterDebounce = useMemo(() => debounce(() => {
    setDebouncedFilter(filter)
  }, DEBOUNCE_DELAY), [filter])

  useEffect(() => {
    filterDebounce()
    return () => {
      filterDebounce.cancel()
    }
  }, [filterDebounce])

  const queryKey = useMemo(
    () => ["filterResults", JSON.stringify(debouncedFilter), modelId, on, currency].join("-"),
    [debouncedFilter, modelId, on, currency],
  )

  return useResource<number>({
    resource: "filterResults",
    url: () => {
      if (isEmpty(debouncedFilter) || !modelId || !on) {
        return null
      }
      return {
        id: queryKey,
        query: FOUND_SECURITIES_QUERY,
        isGraphQl: true,
        variables: { filter: debouncedFilter, modelId, on, currency },
      }
    },
  }, ["model", "securitySearch", "count"])
}
